import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    Divider,
    IconButton,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SensorsIcon from '@mui/icons-material/Sensors';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VerifiedIcon from '@mui/icons-material/Verified';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { campaignWalletUrl, requestPayoutCancelUrl } from '../../configurations/api/url';
import { formatCurrency } from '../../constants/currency';
import { MetaTags } from '../../components/Metatags/Metatags';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { userProfileSelector } from '../Dashboard/selectors';
import {
    BRAND_LUXE_ERROR,
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';
import { BrandDashboardActiveAuditions } from './BrandDashboardActiveAuditions';
import { BrandExpandCardsDivider, BRAND_CARD_GRID_INITIAL_COUNT } from './BrandExpandCardsDivider';
import {
    gettingHostDashboardSelector,
    hostDashboardAggregatesSelector,
    hostDashboardRecentCampaignsSelector,
    hostDashboardSelector,
} from '../../redux/buzzbridge/selectors';
import {
    IHostDashboardAggregates,
    IHostDashboardInsight,
    IHostDashboardRecentCampaign,
} from '../../redux/buzzbridge/types';
import { CampaignThreadUnreadBadge } from '../../components/BuzzBridge/CampaignThreadUnreadBadge';

const radius = '12px';

interface PayoutCancelRequest {
    status: 'pending' | 'approved' | 'rejected';
    reason: string;
    requested_at: string;
    rejection_note: string | null;
}

interface PayoutRecord {
    participant_id: string;
    campaign: { id: string; title: string };
    influencer: {
        id: string;
        username: string | null;
        socials: { instagram: string | null; tiktok: string | null; youtube: string | null };
    };
    payout_amount_cents: number;
    payout_amount: number;
    payout_initiated_at: string;
    payout_release_at: string | null;
    payout_released_at: string | null;
    status: 'pending_release' | 'released';
    cancel_request: PayoutCancelRequest | null;
}

interface PayoutDashboardData {
    total_pending_release_cents: number;
    total_pending_release: number;
    total_released_cents: number;
    total_released: number;
    payout_history: PayoutRecord[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
}

function formatCompactCurrency(value: number): string {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
    return `$${value.toLocaleString()}`;
}

function formatBudget(value: number): string {
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function findHostInsight(insights: IHostDashboardInsight[] | undefined, id: string) {
    return insights?.find(insight => insight.id === id);
}

function brandRatingBadgeLabel(insight: IHostDashboardInsight | undefined): string {
    if (!insight) return 'Brand rating';
    const metrics = insight.metrics;
    if (metrics && typeof metrics === 'object') {
        if (typeof metrics.percentile_label === 'string') return metrics.percentile_label;
        if (typeof metrics.badge_label === 'string') return metrics.badge_label;
        if (typeof metrics.top_percentile === 'number') return `Top ${metrics.top_percentile}% brand rating`;
    }
    if (insight.status === 'pending_data') return insight.title;
    return 'Top 5% brand rating';
}

export default function BrandDashboardHome() {
    const user = useSelector(userProfileSelector);
    const token = useSelector((s: any) => s.authentication.token as string);
    const loading = useSelector(gettingHostDashboardSelector);
    const hostDashboard = useSelector(hostDashboardSelector);
    const aggregates = useSelector(hostDashboardAggregatesSelector);
    const recentCampaigns = useSelector(hostDashboardRecentCampaignsSelector);
    const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || 'there';

    const talentRetentionInsight = useMemo(
        () => findHostInsight(hostDashboard?.insights, 'talent_retention'),
        [hostDashboard?.insights],
    );
    const brandRatingInsight = useMemo(
        () => findHostInsight(hostDashboard?.insights, 'brand_rating'),
        [hostDashboard?.insights],
    );

    const [payoutData, setPayoutData] = useState<PayoutDashboardData | null>(null);
    const [fetchingPayouts, setFetchingPayouts] = useState(false);
    const [payoutModalOpen, setPayoutModalOpen] = useState(false);

    useEffect(() => {
        if (!token) return;
        setFetchingPayouts(true);
        axios
            .get<{ data: PayoutDashboardData }>(`${campaignWalletUrl}/payout-dashboard`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(res => setPayoutData(res.data.data))
            .catch(() => setPayoutData(null))
            .finally(() => setFetchingPayouts(false));
    }, [token]);
    const [campaignsExpanded, setCampaignsExpanded] = useState(false);

    useEffect(() => {
        if (recentCampaigns.length <= BRAND_CARD_GRID_INITIAL_COUNT) {
            setCampaignsExpanded(false);
        }
    }, [recentCampaigns.length]);

    return (
        <MetaTags title="Brand dashboard | Thspian" description="LUXE UGC brand workspace">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    maxWidth: 1280,
                    mx: 'auto',
                    width: '100%',
                }}>
                {/* Welcome header */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { md: 'flex-end' },
                        justifyContent: 'space-between',
                        gap: 3,
                    }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: '2.25rem', md: '3rem' },
                                fontWeight: 900,
                                letterSpacing: '-0.04em',
                                color: '#ffffff',
                                lineHeight: 1.1,
                            }}>
                            Hello,{' '}
                            <Box component="span" sx={{ color: BRAND_LUXE_PRIMARY, fontStyle: 'italic' }}>
                                {displayName}
                            </Box>
                        </Typography>
                        <Typography
                            sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 15, color: '#adaaaa', maxWidth: 420 }}>
                            {aggregates && aggregates.active_campaigns_count > 0 ? (
                                <>
                                    Your brand&apos;s influence is scaling. You have{' '}
                                    <Box
                                        component="span"
                                        sx={{ color: BRAND_LUXE_SECONDARY, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                        {aggregates.active_campaigns_count} Active Campaign
                                        {aggregates.active_campaigns_count !== 1 ? 's' : ''}
                                    </Box>{' '}
                                    running
                                    {aggregates.distinct_brands_in_scope_count > 1
                                        ? ` across ${aggregates.distinct_brands_in_scope_count} brands`
                                        : ''}
                                    .
                                </>
                            ) : (
                                'Launch your first campaign to start growing your brand.'
                            )}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Button
                            id="brand-find-talent-btn"
                            component={Link}
                            to={ROUTES.BRAND_FIND_TALENT}
                            variant="contained"
                            startIcon={<PersonSearchIcon />}
                            sx={{
                                px: 4,
                                py: 1.5,
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 15,
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                color: '#ffffff',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#2c2c2c' },
                            }}>
                            Find more talent
                        </Button>
                        <Button
                            id="brand-create-campaign-btn"
                            component={Link}
                            to={ROUTES.BRAND_CAMPAIGN_NEW}
                            variant="contained"
                            startIcon={<AddCircleIcon />}
                            sx={{
                                px: 4,
                                py: 1.5,
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 15,
                                color: '#392700',
                                background: 'linear-gradient(90deg, ' + BRAND_LUXE_PRIMARY + ', #feb700)',
                                boxShadow: '0 4px 20px rgba(255,201,101,0.3)',
                                '&:hover': {
                                    filter: 'brightness(1.05)',
                                    transform: 'scale(1.02)',
                                },
                            }}>
                            Create new campaign
                        </Button>
                    </Box>
                </Box>

                {/* Key metrics — 3 cards */}
                {loading && !aggregates ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
                    </Box>
                ) : (
                    <Box
                        id="brand-metrics"
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                            gap: 3,
                        }}>
                        <MetricSpend aggregates={aggregates} />
                        <MetricLive aggregates={aggregates} />
                        <MetricPending aggregates={aggregates} />
                    </Box>
                )}

                {/* Active campaigns */}
                <Box id="brand-active-campaigns" sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2,
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 24,
                                fontWeight: 900,
                                letterSpacing: '-0.02em',
                            }}>
                            Active campaigns
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography
                                component={Link}
                                to={`${ROUTES.BRAND_CAMPAIGNS}?status=expired`}
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 14,
                                    color: '#feb700',
                                    textDecoration: 'none',
                                    borderBottom: '1px solid rgba(254,183,0,0.2)',
                                    '&:hover': { borderBottomColor: '#feb700' },
                                }}>
                                View expired
                            </Typography>
                            <Typography
                                component={Link}
                                to={ROUTES.BRAND_CAMPAIGNS}
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 14,
                                    color: '#feb700',
                                    textDecoration: 'none',
                                    borderBottom: '1px solid rgba(254,183,0,0.2)',
                                    '&:hover': { borderBottomColor: '#feb700' },
                                }}>
                                View all
                            </Typography>
                        </Box>
                    </Box>
                    {recentCampaigns.length > 0 ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3 }}>
                            {recentCampaigns.slice(0, BRAND_CARD_GRID_INITIAL_COUNT).map(campaign => (
                                <CampaignCard key={campaign._id} campaign={campaign} />
                            ))}
                            {recentCampaigns.length > BRAND_CARD_GRID_INITIAL_COUNT && (
                                <>
                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <BrandExpandCardsDivider
                                            expanded={campaignsExpanded}
                                            onToggle={() => setCampaignsExpanded(v => !v)}
                                            sectionLabel="campaigns"
                                        />
                                    </Box>
                                    {campaignsExpanded &&
                                        recentCampaigns
                                            .slice(BRAND_CARD_GRID_INITIAL_COUNT)
                                            .map(campaign => <CampaignCard key={campaign._id} campaign={campaign} />)}
                                </>
                            )}
                        </Box>
                    ) : (
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: '#adaaaa' }}>
                            {loading
                                ? 'Loading campaigns…'
                                : 'No campaigns yet. Create your first campaign to get started.'}
                        </Typography>
                    )}
                </Box>

                <BrandDashboardActiveAuditions />

                {/* Insights bento */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
                    <Box
                        sx={{
                            gridColumn: { md: 'span 2' },
                            p: 4,
                            borderRadius: radius,
                            bgcolor: BRAND_SURFACE_CONTAINER,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            minHeight: 200,
                        }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, mb: 1 }}>
                                {talentRetentionInsight?.title ?? 'Talent retention'}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    color: '#adaaaa',
                                    lineHeight: 1.5,
                                }}>
                                {talentRetentionInsight?.body ??
                                    'We are still gathering enough completed campaigns to show your re-hire rate.'}
                            </Typography>
                        </Box>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 2.5 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, mb: 1 }}>
                                {brandRatingInsight?.title ?? 'Brand rating'}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    color: '#adaaaa',
                                    lineHeight: 1.5,
                                    mb: 2.5,
                                }}>
                                {brandRatingInsight?.body ??
                                    'Keep running campaigns and encouraging creators to rate their experience.'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        bgcolor: BRAND_LUXE_SECONDARY + '18',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: BRAND_LUXE_SECONDARY,
                                        flexShrink: 0,
                                    }}>
                                    <VerifiedIcon />
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 15,
                                        color: BRAND_LUXE_SECONDARY,
                                    }}>
                                    {brandRatingBadgeLabel(brandRatingInsight)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            gridColumn: { md: 'span 2' },
                            p: 4,
                            borderRadius: radius,
                            bgcolor: BRAND_SURFACE_CONTAINER,
                            border: '1px solid rgba(255,201,101,0.1)',
                            background: 'linear-gradient(to bottom right, ' + BRAND_SURFACE_CONTAINER + ', #201f1f)',
                        }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18 }}>
                                Upcoming payouts
                            </Typography>
                            <InfoOutlinedIcon sx={{ color: '#adaaaa' }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                            {fetchingPayouts ? (
                                <Skeleton
                                    variant="text"
                                    width={140}
                                    height={52}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 1 }}
                                />
                            ) : (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 36,
                                        fontWeight: 900,
                                        color: BRAND_LUXE_PRIMARY,
                                    }}>
                                    {payoutData ? formatCurrency(payoutData.total_pending_release) : '—'}
                                </Typography>
                            )}
                            {!fetchingPayouts && payoutData && (
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: '#adaaaa' }}>
                                    pending release
                                </Typography>
                            )}
                        </Box>
                        {!fetchingPayouts && payoutData && (
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13, color: '#adaaaa', mt: 1 }}>
                                {formatCurrency(payoutData.total_released)} released to date
                            </Typography>
                        )}
                        <Button
                            fullWidth
                            onClick={() => setPayoutModalOpen(true)}
                            sx={{
                                mt: 3,
                                py: 1.5,
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                bgcolor: '#262626',
                                color: '#ffffff',
                                '&:hover': { bgcolor: '#2c2c2c' },
                            }}>
                            Review payouts
                        </Button>
                    </Box>
                </Box>
            </Box>

            <PayoutHistoryModal
                open={payoutModalOpen}
                onClose={() => setPayoutModalOpen(false)}
                data={payoutData}
                loading={fetchingPayouts}
            />
        </MetaTags>
    );
}

function MetricSpend({ aggregates }: { aggregates: IHostDashboardAggregates | null }) {
    const spend = aggregates?.active_spend_total ?? 0;
    const spendPercentage = aggregates?.active_spend_percent ?? 0;

    return (
        <Box
            sx={{
                p: 4,
                borderRadius: radius,
                bgcolor: BRAND_SURFACE_CONTAINER,
                position: 'relative',
                overflow: 'hidden',
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    right: -32,
                    top: -32,
                    width: 128,
                    height: 128,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,201,101,0.05)',
                    filter: 'blur(24px)',
                }}
            />
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography
                        sx={{ fontSize: 13, color: '#adaaaa', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                        Total active spend
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 11,
                            color: BRAND_LUXE_SECONDARY,
                            bgcolor: BRAND_LUXE_SECONDARY + '18',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                        }}>
                        {spendPercentage}%
                    </Typography>
                </Box>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 48, fontWeight: 900, letterSpacing: '-0.03em' }}>
                    {formatCompactCurrency(spend / 100)}
                </Typography>
                <Box sx={{ height: 4, width: '100%', bgcolor: '#262626', borderRadius: 999, overflow: 'hidden' }}>
                    <Box
                        sx={{
                            height: '100%',
                            width: spend > 0 ? '100%' : '0%',
                            bgcolor: BRAND_LUXE_PRIMARY,
                            boxShadow: '0 0 8px rgba(255,201,101,0.5)',
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
}

function MetricLive({ aggregates }: { aggregates: IHostDashboardAggregates | null }) {
    const active = aggregates?.active_campaigns_count ?? 0;
    const starting48h = aggregates?.campaigns_starting_within_48h_count ?? 0;
    return (
        <Box
            sx={{
                p: 4,
                borderRadius: radius,
                bgcolor: BRAND_SURFACE_CONTAINER,
                position: 'relative',
                overflow: 'hidden',
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    right: -32,
                    top: -32,
                    width: 128,
                    height: 128,
                    borderRadius: '50%',
                    bgcolor: 'rgba(0,253,193,0.05)',
                    filter: 'blur(24px)',
                }}
            />
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography
                        sx={{ fontSize: 13, color: '#adaaaa', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                        Live campaigns
                    </Typography>
                    {active > 0 && <SensorsIcon sx={{ color: BRAND_LUXE_SECONDARY, fontSize: 22 }} />}
                </Box>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 48, fontWeight: 900, letterSpacing: '-0.03em' }}>
                    {active}
                </Typography>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: '#adaaaa' }}>
                    {starting48h > 0 ? `${starting48h} starting in the next 48 hours` : 'No campaigns starting soon'}
                </Typography>
            </Box>
        </Box>
    );
}

function MetricPending({ aggregates }: { aggregates: IHostDashboardAggregates | null }) {
    const pendingJoins = aggregates?.pending_join_requests_count ?? 0;
    const pendingContent = aggregates?.pending_content_review_count ?? 0;
    const total = pendingJoins + pendingContent;
    const allAvatars = [...(aggregates?.pending_join_avatars ?? []), ...(aggregates?.pending_content_avatars ?? [])];
    const visibleAvatars = allAvatars.slice(0, 2);
    const overflow = allAvatars.length - visibleAvatars.length;
    return (
        <Box
            sx={{
                p: 4,
                borderRadius: radius,
                bgcolor: BRAND_SURFACE_CONTAINER,
                position: 'relative',
                overflow: 'hidden',
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    right: -32,
                    top: -32,
                    width: 128,
                    height: 128,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,115,81,0.05)',
                    filter: 'blur(24px)',
                }}
            />
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography
                        sx={{ fontSize: 13, color: '#adaaaa', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                        Pending review
                    </Typography>
                    {allAvatars.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0 }}>
                            {visibleAvatars.map((avatar, i) => (
                                <Box
                                    key={i}
                                    component="img"
                                    src={avatar.profile_picture}
                                    alt={avatar.name}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        border: '2px solid #0e0e0e',
                                        ml: i === 0 ? 0 : -0.5,
                                    }}
                                />
                            ))}
                            {overflow > 0 && (
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        border: '2px solid #0e0e0e',
                                        bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                        ml: -0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    }}>
                                    +{overflow}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 48, fontWeight: 900, letterSpacing: '-0.03em' }}>
                    {total}
                </Typography>
                {total > 0 ? (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            color: BRAND_LUXE_ERROR,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}>
                        <ScheduleIcon sx={{ fontSize: 18 }} />
                        Action required
                    </Typography>
                ) : (
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: '#adaaaa' }}>
                        All caught up
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function PayoutHistoryModal({
    open,
    onClose,
    data,
    loading,
}: {
    open: boolean;
    onClose: () => void;
    data: PayoutDashboardData | null;
    loading: boolean;
}) {
    const token = useSelector((s: any) => s.authentication.token as string);
    const [cancellingId, setCancellingId] = useState<string | null>(null);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [cancelledIds, setCancelledIds] = useState<Set<string>>(new Set());

    const handleRequestCancel = async (participantId: string) => {
        if (!reason.trim()) return;
        setSubmitting(true);
        try {
            await axios.post(
                requestPayoutCancelUrl(participantId),
                { reason: reason.trim() },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setCancelledIds(prev => new Set(prev).add(participantId));
            setCancellingId(null);
            setReason('');
        } catch (_) {
            // keep panel open on error so user can retry
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#0e0e0e',
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.07)',
                    m: 2,
                    overflow: 'hidden',
                },
            }}>
            {/* Header */}
            <Box sx={{ px: 3, pt: 3, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#f5f5f5' }}>
                    Payout History
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ color: '#7a7a7a', '&:hover': { color: '#f5f5f5' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Summary row */}
            {data && (
                <>
                    <Box sx={{ px: 3, pb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: '#7a7a7a',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}>
                                Pending release
                            </Typography>
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, color: BRAND_LUXE_PRIMARY }}>
                                {formatCurrency(data.total_pending_release)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: '#7a7a7a',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}>
                                Released
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 22, color: '#00fdc1' }}>
                                {formatCurrency(data.total_released)}
                            </Typography>
                        </Box>
                    </Box>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />
                </>
            )}

            {/* List */}
            <Box sx={{ px: 3, pt: 2, pb: 3, maxHeight: '55vh', overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} sx={{ color: BRAND_LUXE_PRIMARY }} />
                    </Box>
                ) : !data || data.payout_history.length === 0 ? (
                    <Typography
                        sx={{ textAlign: 'center', color: '#7a7a7a', py: 6, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        No payout records yet
                    </Typography>
                ) : (
                    <Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {data.payout_history.map(record => {
                                const isPending = record.status === 'pending_release';
                                // cancelRequest: from API first, then optimistic local state
                                const cancelRequest = record.cancel_request;
                                const justCancelled = cancelledIds.has(record.participant_id);
                                const hasCancelRequest = cancelRequest != null || justCancelled;
                                const cancelStatus = cancelRequest?.status ?? (justCancelled ? 'pending' : null);

                                const statusColor =
                                    cancelStatus === 'pending'
                                        ? '#f5a623'
                                        : cancelStatus === 'approved'
                                        ? '#ff6b6b'
                                        : cancelStatus === 'rejected'
                                        ? '#7a7a7a'
                                        : isPending
                                        ? BRAND_LUXE_PRIMARY
                                        : '#00fdc1';

                                const statusLabel =
                                    cancelStatus === 'pending'
                                        ? 'Cancel pending review'
                                        : cancelStatus === 'approved'
                                        ? 'Cancellation approved'
                                        : cancelStatus === 'rejected'
                                        ? 'Cancellation rejected'
                                        : isPending
                                        ? 'Pending'
                                        : 'Released';

                                const { socials, username } = record.influencer;
                                const handle =
                                    socials?.instagram ?? socials?.tiktok ?? socials?.youtube ?? username ?? '—';
                                const isExpanded = cancellingId === record.participant_id;
                                const initiatedDate = new Date(record.payout_initiated_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                });
                                const releaseDate = record.payout_release_at
                                    ? new Date(record.payout_release_at).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                      })
                                    : null;
                                return (
                                    <Box key={record.participant_id}>
                                        {/* Row */}
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr auto',
                                                gap: 2,
                                                alignItems: 'center',
                                                px: 1,
                                                py: 1.75,
                                                borderRadius: 2,
                                                transition: 'background 0.15s',
                                                '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                                            }}>
                                            {/* Creator cell */}
                                            <Box sx={{ minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box
                                                        sx={{
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: '50%',
                                                            bgcolor: statusColor,
                                                            flexShrink: 0,
                                                            boxShadow: `0 0 6px ${statusColor}88`,
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontSize: 13,
                                                            color: '#e8e8e8',
                                                        }}
                                                        noWrap>
                                                        {handle}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    sx={{ fontSize: 10, color: '#4a4a4a', mt: 0.25, pl: '14px' }}>
                                                    {statusLabel} · {initiatedDate}
                                                </Typography>
                                            </Box>

                                            {/* Campaign cell */}
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 12, color: '#7a7a7a' }} noWrap>
                                                    {record.campaign.title}
                                                </Typography>
                                                {releaseDate && isPending && (
                                                    <Typography sx={{ fontSize: 10, color: '#3a3a3a', mt: 0.25 }}>
                                                        Releases {releaseDate}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Amount + action cell */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    justifyContent: 'flex-end',
                                                }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 14,
                                                        color: '#f0f0f0',
                                                        letterSpacing: '-0.02em',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                    {formatCurrency(record.payout_amount)}
                                                </Typography>
                                                {isPending && !hasCancelRequest && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            setCancellingId(prev =>
                                                                prev === record.participant_id
                                                                    ? null
                                                                    : record.participant_id,
                                                            );
                                                            setReason('');
                                                        }}
                                                        sx={{
                                                            fontSize: 10,
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            color: '#ff6b6b',
                                                            textTransform: 'none',
                                                            px: 1.5,
                                                            py: 0.4,
                                                            minWidth: 0,
                                                            whiteSpace: 'nowrap',
                                                            border: '1px solid rgba(255,107,107,0.3)',
                                                            borderRadius: 999,
                                                            bgcolor: isExpanded
                                                                ? 'rgba(255,107,107,0.1)'
                                                                : 'transparent',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(255,107,107,0.1)',
                                                                borderColor: '#ff6b6b',
                                                            },
                                                        }}>
                                                        Cancel payout
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Cancel reason panel */}
                                        {isExpanded && (
                                            <Box
                                                sx={{
                                                    mx: 1,
                                                    mb: 1,
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: '#111',
                                                    border: '1px solid rgba(255,107,107,0.15)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1,
                                                }}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    multiline
                                                    minRows={2}
                                                    placeholder="Reason for cancellation…"
                                                    value={reason}
                                                    onChange={e => setReason(e.target.value)}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            bgcolor: '#161616',
                                                            color: '#f5f5f5',
                                                            fontSize: 12,
                                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.06)' },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(255,107,107,0.3)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: 'rgba(255,107,107,0.5)',
                                                            },
                                                        },
                                                        '& .MuiInputBase-input::placeholder': {
                                                            color: '#4a4a4a',
                                                            opacity: 1,
                                                        },
                                                    }}
                                                />
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            setCancellingId(null);
                                                            setReason('');
                                                        }}
                                                        sx={{ fontSize: 11, color: '#4a4a4a', textTransform: 'none' }}>
                                                        Dismiss
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        disabled={!reason.trim() || submitting}
                                                        onClick={() => handleRequestCancel(record.participant_id)}
                                                        sx={{
                                                            fontSize: 11,
                                                            textTransform: 'none',
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            bgcolor: 'rgba(255,107,107,0.12)',
                                                            color: '#ff6b6b',
                                                            px: 2,
                                                            borderRadius: 1,
                                                            '&:hover': { bgcolor: 'rgba(255,107,107,0.2)' },
                                                            '&.Mui-disabled': {
                                                                color: '#3a3a3a',
                                                                bgcolor: 'transparent',
                                                            },
                                                        }}>
                                                        {submitting ? (
                                                            <CircularProgress size={11} sx={{ color: '#ff6b6b' }} />
                                                        ) : (
                                                            'Submit'
                                                        )}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
}

function CampaignCard({ campaign }: { campaign: IHostDashboardRecentCampaign }) {
    const navigate = useNavigate();
    const {
        _id,
        title,
        primary_product,
        feature_image,
        is_active,
        status,
        total_budget,
        participants_active,
        participants_total,
        total_participants_count,
        completion_percent,
        end_date,
    } = campaign;
    const isExpired = status === 'paused' || (Boolean(end_date) && new Date(end_date as string).getTime() < Date.now());
    const badgeLabel = is_active ? 'Active' : isExpired ? 'Expired' : status === 'draft' ? 'Draft' : 'Published';
    const badgeColor = is_active ? BRAND_LUXE_SECONDARY : isExpired ? '#a0a0a0' : BRAND_LUXE_PRIMARY;
    const barColor = is_active ? BRAND_LUXE_SECONDARY : BRAND_LUXE_PRIMARY;

    return (
        <Box
            role="button"
            tabIndex={0}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', _id));
                }
            }}
            onClick={() => navigate(ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', _id))}
            sx={{
                p: 3,
                borderRadius: radius,
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                cursor: 'pointer',
                transition: 'background 0.2s',
                '&:hover': { bgcolor: BRAND_SURFACE_CONTAINER },
            }}>
            {feature_image && (
                <Box
                    sx={{
                        width: { xs: '100%', md: 128 },
                        height: 128,
                        borderRadius: radius,
                        overflow: 'hidden',
                        flexShrink: 0,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                    }}>
                    <Box
                        component="img"
                        src={feature_image}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </Box>
            )}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20 }}>{title}</Typography>
                            <CampaignThreadUnreadBadge count={campaign.unread_messages_total} size="sm" />
                        </Box>
                        {primary_product && (
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: '#adaaaa', mt: 0.5 }}>
                                Product: {primary_product}
                            </Typography>
                        )}
                    </Box>
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            color: badgeColor,
                            bgcolor: badgeColor + '18',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            whiteSpace: 'nowrap',
                        }}>
                        {badgeLabel}
                    </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: 10, color: '#adaaaa', textTransform: 'uppercase' }}>
                            Budget
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15 }}>
                            {formatBudget(total_budget)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 10, color: '#adaaaa', textTransform: 'uppercase' }}>
                            Creators
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15 }}>
                            {participants_active} / {total_participants_count ?? participants_total}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: '#adaaaa',
                                textTransform: 'uppercase',
                            }}>
                            Completion
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontFamily: FONT_ENUM.MAIN_BOLD, color: barColor }}>
                            {completion_percent}%
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            height: 6,
                            width: '100%',
                            bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                            borderRadius: 999,
                            overflow: 'hidden',
                        }}>
                        <Box
                            sx={{
                                height: '100%',
                                width: `${completion_percent}%`,
                                bgcolor: barColor,
                                boxShadow: is_active
                                    ? '0 0 10px rgba(0,253,193,0.3)'
                                    : '0 0 10px rgba(255,201,101,0.3)',
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
